<script lang="ts" setup>
import IconInfo from '@/components/Icons/IconInfo.vue'
import IconError from '@/components/Icons/IconError.vue'
import IconSuccess from '@/components/Icons/IconSuccess.vue'
import IconWarning from '@/components/Icons/IconWarningLarge.vue'

const props = withDefaults(
  defineProps<{
    show?: boolean
    size?: 'default' | 'small'
    theme?: 'info' | 'warning' | 'success' | 'error'
    dismissible?: boolean
    centerIcon?: boolean
  }>(),
  {
    show: true,
    size: 'default',
    theme: 'info',
    dismissible: false,
    centerIcon: false,
  },
)

const dismissed = ref(false)

// Reset dismissed state when show changes
watch(
  () => props.show,
  value => {
    if (value) {
      dismissed.value = false
    }
  },
)

const visible = computed(() => props.show && !dismissed.value)
const iconComponent = computed(() => {
  switch (props.theme) {
    case 'info':
      return IconInfo
    case 'warning':
      return IconWarning
    case 'success':
      return IconSuccess
    case 'error':
      return IconError
  }
})
</script>

<template>
  <SlideVertical>
    <div v-if="visible" class="@container">
      <div
        class="-mx-[var(--container-padding)] my-4 flex rounded border px-[var(--container-padding)] font-normal @sm:flex-row s:mx-0"
        :class="{
          'items-start': !centerIcon,
          'items-center': centerIcon,
          'flex-col gap-3 py-3 @sm:gap-5 @sm:p-6 @sm:py-6 s:px-3': size === 'default',
          'gap-3 py-3 text-sm @sm:p-4 s:px-2': size === 'small',
          'border-blue-200 bg-blue-50 text-blue-800': theme === 'info',
          'border-yellow-200 bg-yellow-100 text-yellow-900': theme === 'warning',
          'border-green-200 bg-green-50 text-green-800': theme === 'success',
          'border-red-200 bg-red-100 text-red-900': theme === 'error',
        }"
        role="alert"
      >
        <component
          :is="iconComponent"
          class="w-auto shrink-0"
          :class="{ 'h-5 @sm:h-6': size === 'default', 'mt-px h-4': size === 'small' }"
        />
        <div class="trim-spacings">
          <slot />
        </div>
        <Button
          v-if="dismissible"
          class="ml-auto shrink-0 p-2"
          theme="unstyled"
          @click="dismissed = true"
        >
          <span class="sr-only">Schliessen</span>
          <IconX />
        </Button>
      </div>
    </div>
  </SlideVertical>
</template>
